import React from 'react';
import { motion } from 'framer-motion';
import { TOKENS } from '../tokens';

function Particle({ style }) {
  return (
    <motion.div
      style={{
        position: 'absolute',
        borderRadius: '50%',
        background: '#987315',
        width: style.size,
        height: style.size,
        left: style.left,
        bottom: '-20px',
        pointerEvents: 'none',
      }}
      animate={{
        y: [0, -900],
        opacity: [0, 0.9, 0.8, 0.5, 0],
        scale: [0, 1.2, 1, 0.8, 0],
      }}
      transition={{
        duration: style.duration,
        delay: style.delay,
        repeat: Infinity,
        ease: 'linear',
      }}
    />
  );
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay },
});

export default function Hero() {
  const particles = Array.from({ length: 40 }, () => ({
    left: `${Math.random() * 100}%`,
    size: `${Math.random() * 3 + 1}px`,
    duration: Math.random() * 8 + 6,
    delay: Math.random() * 4,
  }));

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <section id="hero" style={{
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      overflow: 'hidden',
      background: '#050A1A',
      paddingBottom: 140,
    }}>
      <style>{`
        @keyframes meshPulse { 0%,100%{opacity:.6} 50%{opacity:1} }
        @keyframes dotBounce {
          0%,100%{transform:translateY(0);opacity:.4}
          50%{transform:translateY(6px);opacity:1}
        }
        @keyframes gradientShift {
          0%,100%{background-position:0% 50%}
          50%{background-position:100% 50%}
        }
        /* ── Mobile fixes ── */
        @media (max-width: 600px) {
          .hero-stats-bar { display: none !important; }
          .hero-inner {
            padding: 88px 20px 0 !important;
            text-align: center !important;
            align-items: center !important;
          }
          .hero-eyebrow {
            font-size: 0.6rem !important;
            letter-spacing: 0.14em !important;
          }
          .hero-btn-row {
            flex-direction: column !important;
            align-items: center !important;
            width: 100% !important;
          }
          .hero-btn-gold, .hero-btn-outline {
            width: 100% !important;
            max-width: 320px !important;
            justify-content: center !important;
            cursor: auto !important;
          }
        }
      `}</style>

      {/* Ambient mesh */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `
          radial-gradient(ellipse 80% 60% at 50% 0%, rgba(200,169,106,0.09) 0%, transparent 60%),
          radial-gradient(ellipse 60% 80% at 15% 80%, rgba(200,169,106,0.05) 0%, transparent 50%),
          radial-gradient(ellipse 60% 80% at 85% 80%, rgba(200,169,106,0.05) 0%, transparent 50%)
        `,
        animation: 'meshPulse 8s ease-in-out infinite',
      }} />

      {/* Grid lines */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `
          linear-gradient(rgba(200,169,106,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(200,169,106,0.03) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, #000 20%, transparent 80%)',
        maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, #000 20%, transparent 80%)',
        pointerEvents: 'none',
      }} />

      {/* Gold Particles */}
      <div style={{
        position: 'absolute', inset: 0,
        pointerEvents: 'none',
        zIndex: 1,
      }}>
        {particles.map((p, i) => (
          <Particle key={i} style={p} />
        ))}
      </div>

      {/* ── Main content ── */}
      <div
        className="hero-inner"
        style={{
          position: 'relative', zIndex: 2,
          padding: '80px 24px 0',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        {/* Eyebrow */}
        <motion.p
          {...fadeUp(0.3)}
          className="hero-eyebrow"
          style={{
            fontSize: '0.68rem', fontWeight: 400,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: TOKENS.GOLD, marginBottom: 24,
            textAlign: 'center',
          }}
        >
          A Rare Commercial Opportunity · Central Delhi
        </motion.p>

        {/* Headline */}
        <motion.h1
          {...fadeUp(0.45)}
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(2.8rem, 7vw, 7rem)',
            fontWeight: 300, lineHeight: 1.05,
            letterSpacing: '-0.01em',
            color: '#fff', marginBottom: 24,
            textAlign: 'center',
            width: '100%',
          }}
        >
          The Property<br />That Changes<br />
          <span style={{
            fontStyle: 'italic',
            background: 'linear-gradient(135deg, #C8A96A 0%, #E8C97A 50%, #C8A96A 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundSize: '200% 200%',
            animation: 'gradientShift 4s ease infinite',
          }}>
            Everything.
          </span>
        </motion.h1>

        {/* Subline */}
        <motion.p
          {...fadeUp(0.6)}
          style={{
            fontSize: 'clamp(0.9rem, 2vw, 1.05rem)',
            fontWeight: 300,
            color: 'rgba(255,255,255,0.52)',
            letterSpacing: '0.02em',
            marginBottom: 40,
            lineHeight: 1.65,
            textAlign: 'center',
          }}
        >
          225 Sq. Yard Commercial Ground Floor Jhandewalan<br />
          Separate entrance · Occupied · Rental income from Day 1.
        </motion.p>

        {/* Buttons */}
        <motion.div
          {...fadeUp(0.75)}
          className="hero-btn-row"
          style={{
            display: 'flex', gap: 14,
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: 44,
            width: '100%',
          }}
        >
          <button
            onClick={() => scrollTo('the-details')}
            className="hero-btn-gold"
            style={btnGold}
            onMouseEnter={e => {
              e.currentTarget.style.background = TOKENS.GOLD_LIGHT;
              e.currentTarget.style.transform = 'scale(1.04)';
              e.currentTarget.style.boxShadow = '0 0 40px rgba(200,169,106,0.3)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = TOKENS.GOLD;
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Discover the Property
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <button
            onClick={() => scrollTo('contact')}
            className="hero-btn-outline"
            style={btnOutline}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.13)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.26)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
            }}
          >
            Schedule a Visit
          </button>
        </motion.div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 0.8 }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
            fontSize: '0.58rem', letterSpacing: '0.2em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.28)',
          }}
        >
          <div style={{
            width: 22, height: 40,
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 11,
            display: 'flex', justifyContent: 'center', paddingTop: 6,
          }}>
            <div style={{
              width: 3, height: 6, borderRadius: 2,
              background: 'rgba(255,255,255,0.45)',
              animation: 'dotBounce 2s ease infinite',
            }} />
          </div>
          Scroll to explore
        </motion.div>
      </div>

      {/* Stats bar */}
      <motion.div
        className="hero-stats-bar"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          display: 'flex',
          borderTop: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        {[
          { val: '225', label: 'Square Yards' },
          { val: '₹5.55 Cr', label: 'Asking Price' },
          { val: 'Jhandewalan', label: 'Central Delhi' },
          { val: 'Industrial', label: 'Commercial Use' },
        ].map((s, i) => (
          <div key={i} style={{
            flex: 1, textAlign: 'center',
            padding: '20px 16px',
            borderRight: i < 3 ? '1px solid rgba(255,255,255,0.07)' : 'none',
            background: 'rgba(5,10,26,0.7)',
            backdropFilter: 'blur(10px)',
          }}>
            <span style={{
              display: 'block',
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(0.9rem, 2vw, 1.35rem)',
              fontWeight: 300, color: '#fff', marginBottom: 4,
            }}>{s.val}</span>
            <span style={{
              fontSize: '0.55rem', letterSpacing: '0.14em',
              textTransform: 'uppercase', color: TOKENS.MUTED,
            }}>{s.label}</span>
          </div>
        ))}
      </motion.div>
    </section>
  );
}

const btnGold = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '14px 28px',
  background: '#C8A96A', color: '#000',
  fontFamily: "'Outfit', sans-serif",
  fontSize: '0.82rem', fontWeight: 500, letterSpacing: '0.04em',
  borderRadius: 980, border: 'none', cursor: 'none',
  transition: 'all 0.3s cubic-bezier(0.16,1,0.3,1)',
};

const btnOutline = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '14px 28px',
  background: 'rgba(255,255,255,0.07)', color: '#fff',
  fontFamily: "'Outfit', sans-serif",
  fontSize: '0.82rem', fontWeight: 400, letterSpacing: '0.04em',
  borderRadius: 980, border: '1px solid rgba(255,255,255,0.12)',
  cursor: 'none', transition: 'all 0.3s',
};
